import { Formik, type FormikConfig } from 'formik'
import type { FormikHelpers } from 'formik/dist/types'
import React, { type ReactNode } from 'react'

import { Dashboard } from '@components/common'
import { NavTabs } from '@components/common/NavTabs'
import { DashboardPermissionGuard } from '@components/utils/DashboardPermissions'
import { LeavingGuard } from '@components/utils/LeavingGuard'
import {
  AddButton,
  BaseContent,
  Button,
  ButtonClean,
  HeadContent,
  Heading,
  Loading,
  Pagination,
  type PaginationProps,
  PriceString,
  SaveShortcutListener,
  Subsection,
} from '@fourthwall/components'
import type { Money } from '@fourthwall/services/dist/models/common'
import { ConfirmBar } from '@modules/Dashboard/modules/ConfirmBar'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import { routing } from '@utils/routing'

import { ChargeLimitFormGroup } from '../../../../modules/PaymentMethods'
import * as S from './Billing.styled'
import type {
  ButtonType,
  ChargesHistoryDataItem,
  FormValues,
  PayoutsHistoryDataItem,
  PayoutsSetup,
} from './Billing.types'
import { ChargesHistoryData } from './components/ChargesHistoryData/ChargesHistoryData'
import { PayoutsHistoryData } from './components/PayoutsHistoryData/PayoutsHistoryData'
import { getSetupBy } from './utils/getSetupBy'
import { getSetupTitle } from './utils/getSetupTitle'

export type BillingViewProps = {
  initialValues: FormValues
  validationSchema: FormikConfig<FormValues>['validationSchema']
  accountBalance: {
    value?: number
  }
  payoutsSetup: {
    setup: PayoutsSetup
    isLoading: boolean
  }
  paymentMethods: {
    list: ReactNode
    buttonType: ButtonType
    chargedCurrentMonth?: Money
    onAdd: () => void
  }
  tabsProps: {
    tabs: { label: string; value: string }[]
    activeTab: string
    onTabClick: (tab: string) => void
  }
  payoutsHistory: {
    data: PayoutsHistoryDataItem[]
    isLoading: boolean
    pagination: PaginationProps | undefined
  }
  chargesHistory: {
    data: ChargesHistoryDataItem[]
    isLoading: boolean
    pagination: PaginationProps | undefined
  }
  isSubmitting: boolean
  onSubmit: (values: FormValues, formikHelpers: FormikHelpers<FormValues>) => void
}

export const BillingView = ({
  initialValues,
  validationSchema,
  accountBalance,
  payoutsSetup,
  paymentMethods,
  tabsProps,
  payoutsHistory,
  chargesHistory,
  isSubmitting,
  onSubmit,
}: BillingViewProps) => {
  const { timezone } = useShopTime()
  const renderPayoutsSetup = () => {
    const { setup } = payoutsSetup

    const under25Text =
      typeof accountBalance.value === 'number' && accountBalance.value <= 25
        ? ' (as long as you’ve earned over $25)'
        : ''

    if (setup.status === 'INACTIVE') {
      return (
        <>
          <BaseContent>
            At the start of each month we will transfer your earnings to a bank account of your
            choosing{under25Text}.{' '}
            <a
              href="https://help.fourthwall.com/hc/en-us/articles/13331326910363-How-do-I-get-paid"
              target="_blank"
              rel="noreferrer"
            >
              Learn more
            </a>
          </BaseContent>
          {setup.onSetup && (
            <div style={{ marginTop: 16 }}>
              <Button label="Set up your payouts" appearance="primary" onClick={setup.onSetup} />
            </div>
          )}
        </>
      )
    }

    if (setup.status === 'ACTIVE') {
      return (
        <>
          <BaseContent>{getSetupTitle(setup.inProgress, setup.info.partnerName)}</BaseContent>
          {setup.onEdit && (
            <div style={{ marginTop: 4 }}>
              <ButtonClean label="Edit bank connection" onClick={setup.onEdit} />
            </div>
          )}
          {setup.onNewSetup && (
            <div style={{ marginTop: 4 }}>
              <ButtonClean label="Set up new payout bank account" onClick={setup.onNewSetup} />
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <BaseContent appearance="secondary" size="small">
              Set up by {getSetupBy(setup.info.ownerEmail, setup.info.ownerName)} on{' '}
              <TimezonedDateString date={setup.info.date} format="DD MMM, YYYY" />.
            </BaseContent>
          </div>
        </>
      )
    }

    return null
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={onSubmit}
    >
      {({ values, dirty, isValid, submitForm, resetForm, handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <HeadContent title="Billing and payouts &ndash; Settings" />

          <Subsection title="Account balance">
            {accountBalance.value !== undefined && (
              <div style={{ marginBottom: 16 }}>
                <Heading type="h2" asTag="h5" weight="bold">
                  <PriceString value={accountBalance.value} />
                </Heading>
              </div>
            )}
            <DashboardPermissionGuard permission="settings.billing.payouts">
              <Loading isLoading={payoutsSetup.isLoading}>{renderPayoutsSetup()}</Loading>
            </DashboardPermissionGuard>
          </Subsection>

          <DashboardPermissionGuard permission="settings.billing.paymentMethods">
            <Subsection title="Payment methods">
              <S.SectionDescription>
                Cover giveaways, refunds, and other actions that cost money. You will only be
                charged if your account balance does not cover the cost.{' '}
                <a
                  href={routing.external.helpCenter.creditCard}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more
                </a>
              </S.SectionDescription>

              {paymentMethods.list}

              <div style={{ marginTop: 16 }}>
                {paymentMethods.buttonType === 'primary' && (
                  <Button
                    label="Add a payment method"
                    appearance="primary"
                    onClick={paymentMethods.onAdd}
                  />
                )}

                {paymentMethods.buttonType === 'add' && (
                  <AddButton label="Add a payment method" onClick={paymentMethods.onAdd} />
                )}
              </div>

              <S.SpendingLimitContainer>
                <ChargeLimitFormGroup
                  isEnabled={values.setChargeLimit}
                  currentMonthSpending={paymentMethods.chargedCurrentMonth}
                />
              </S.SpendingLimitContainer>
            </Subsection>
          </DashboardPermissionGuard>
          <Subsection borderless>
            <Dashboard.Section.Item>
              <S.TabsContainer>
                <NavTabs {...tabsProps} />
                <DashboardPermissionGuard permission="settings.billing.history.full">
                  <ButtonClean
                    label="View full history"
                    to={routing.settings.billing.transactions}
                  />
                </DashboardPermissionGuard>
              </S.TabsContainer>
            </Dashboard.Section.Item>

            {tabsProps.activeTab === 'payouts' && (
              <>
                {timezone !== 'UTC' && (
                  <Dashboard.Section.Item>
                    <p>
                      Payouts are calculated in the UTC time zone.{' '}
                      <ButtonClean
                        label="Check payouts FAQ"
                        href={routing.external.helpCenter.payouts}
                        target="_blank"
                      />
                    </p>
                  </Dashboard.Section.Item>
                )}

                <Dashboard.Section.Item>
                  <S.TableWrapper>
                    <PayoutsHistoryData
                      data={payoutsHistory.data}
                      isLoading={payoutsHistory.isLoading}
                    />
                    {payoutsHistory.pagination && <Pagination {...payoutsHistory.pagination} />}
                  </S.TableWrapper>
                </Dashboard.Section.Item>
              </>
            )}

            {tabsProps.activeTab === 'charges' && (
              <Dashboard.Section.Item>
                <S.TableWrapper>
                  <ChargesHistoryData
                    data={chargesHistory.data}
                    isLoading={chargesHistory.isLoading}
                  />
                  {chargesHistory.pagination && <Pagination {...chargesHistory.pagination} />}
                </S.TableWrapper>
              </Dashboard.Section.Item>
            )}
          </Subsection>

          <SaveShortcutListener disabled={!dirty} onSave={submitForm} />
          <ConfirmBar
            isOpen={dirty || isSubmitting}
            onConfirm={submitForm}
            onCancel={resetForm}
            isLoading={isSubmitting}
          />
          <LeavingGuard when={dirty} isValid={isValid} onSave={submitForm} />
        </form>
      )}
    </Formik>
  )
}
